import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  fetchOmnichannelStatus,
  fetchOmnichannelAccounts,
  fetchOmnichannelConversations,
  fetchConversationMessages,
} from './request';
import { fetchLatestConversationMessagesPage } from '@/lib/omnichannel/fetch-latest-messages-page';
import { omnichannelKeys } from './key';
import type { ChannelType, PaginatedMessagesResponse } from '@/types';

export const useOmnichannelStatus = (enabled = true) => {
  return useQuery({
    queryKey: omnichannelKeys.status(),
    queryFn: fetchOmnichannelStatus,
    enabled,
  });
};

export const useOmnichannelAccounts = (params: {
  channel: ChannelType;
  enabled?: boolean;
}) => {
  const { channel, enabled = true } = params;

  return useQuery({
    queryKey: omnichannelKeys.accounts(channel),
    queryFn: () => fetchOmnichannelAccounts({ channel }),
    enabled,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useOmnichannelConversations = (params: {
  channel: ChannelType;
  pageId?: string;
  page?: number;
  enabled?: boolean;
  refetchInterval?: number | false;
}) => {
  const {
    channel,
    pageId,
    page,
    enabled = true,
    refetchInterval = false,
  } = params;

  return useQuery({
    queryKey: omnichannelKeys.conversations(channel, pageId, page),
    queryFn: () => fetchOmnichannelConversations({ channel, pageId, page }),
    enabled,
    refetchInterval,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 15_000,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 429) return false;
      return failureCount < 1;
    },
  });
};

export const useConversationMessages = (params: {
  conversationId: string;
  channel: ChannelType;
  pageId?: string;
  enabled?: boolean;
}) => {
  const { conversationId, channel, pageId, enabled = true } = params;

  return useInfiniteQuery<PaginatedMessagesResponse>({
    queryKey: omnichannelKeys.messages(conversationId, channel, pageId),
    initialPageParam: -1,
    queryFn: async ({ pageParam }) => {
      if (pageParam === -1) {
        return fetchLatestConversationMessagesPage(conversationId, {
          channel,
          pageId,
        });
      }

      return fetchConversationMessages(conversationId, {
        channel,
        pageId,
        page: Number(pageParam) || 1,
      });
    },
    // We paginate backwards (older messages) from the latest page.
    getNextPageParam: () => undefined,
    getPreviousPageParam: (firstPage) => {
      const current = firstPage?.currentPage ?? 1;
      return current > 1 ? current - 1 : undefined;
    },
    enabled: enabled && !!conversationId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
